/**
 * NUMBER INPUTS — one pattern for every <input type="number">.
 *
 * - No spinner arrows (index.html hides them; they covered the value).
 * - The native `step` is always "any", so a typed value with more decimals than
 *   the step is never rounded or flagged invalid (a form would refuse to
 *   submit it). The step that matters lives in `data-step` and is used only by
 *   the ↑/↓ keys (installNumberStepping below).
 * - inputMode "decimal" gives phones the number keypad. A field that takes a
 *   negative passes { negative: true } and keeps the default keyboard: the iOS
 *   decimal keypad has no minus key.
 *
 * Steps: per-lb rates 0.01 (STEP_PER_LB); hourly rates, dollar amounts and
 * counts 1; percentages and hours keep their own.
 */

export const STEP_PER_LB = 0.01;

export function numberInput(step: number = 1, opts: { negative?: boolean } = {}) {
  return {
    type: 'number',
    step: 'any',
    'data-step': String(step),
    inputMode: opts.negative ? undefined : ('decimal' as const),
  };
}

const decimalsOf = (s: string) => {
  const i = s.indexOf('.');
  return i < 0 ? 0 : s.length - i - 1;
};

/**
 * ↑/↓ on a number input adds or takes one `data-step` (1 when unset). Keeps
 * as many decimals as the value already has, so 3.456 + 0.01 is 3.466, not
 * 3.47. Honours min/max. Sets the value the way React sees it (native setter +
 * an input event), so onChange fires as if typed. A component that handles the
 * arrows itself calls preventDefault and is left alone.
 */
export function installNumberStepping() {
  const setValue = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')!.set!;
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    if (e.defaultPrevented) return;
    const el = e.target;
    if (!(el instanceof HTMLInputElement) || el.type !== 'number' || el.readOnly || el.disabled) return;
    e.preventDefault();
    const raw = el.value.trim();
    const cur = raw === '' ? 0 : Number(raw);
    if (!Number.isFinite(cur)) return;
    const stepAttr = el.dataset.step ?? (el.step && el.step !== 'any' ? el.step : '1');
    const step = Number(stepAttr) || 1;
    let next = cur + (e.key === 'ArrowUp' ? step : -step);
    if (el.min !== '' && Number.isFinite(Number(el.min))) next = Math.max(Number(el.min), next);
    if (el.max !== '' && Number.isFinite(Number(el.max))) next = Math.min(Number(el.max), next);
    const text = next.toFixed(Math.max(decimalsOf(raw), decimalsOf(stepAttr)));
    setValue.call(el, text);
    el.dispatchEvent(new Event('input', { bubbles: true }));
  });
}
